import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  emailService,
  EmailAccountConnection,
  EmailTemplate,
  EmailTemplateCategory,
  EmailTemplateWritePayload,
  EMAIL_TEMPLATE_CATEGORIES,
} from '../services/emailService';
import { Link } from 'react-router-dom';
import { ApiError } from '../services/apiClient';
import { getReconnectErrorMeta } from '../utils/emailErrors';
import { useAuth } from '../contexts/AuthContext';
import { EMAIL_TEMPLATE_MERGE_FIELDS } from '../utils/emailTemplateVars';
import { sequenceService } from '../services/sequenceService';

const EMPTY_TEMPLATE_FORM: EmailTemplateWritePayload = {
  name: '',
  category: 'general',
  subject_template: '',
  body_template: '',
  description: '',
  default_follow_up_delay_days: null,
  is_shared: false,
  is_active: true,
};

const categoryLabel = (value: string) =>
  EMAIL_TEMPLATE_CATEGORIES.find((item) => item.value === value)?.label ?? value;

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const EmailSettings: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [accounts, setAccounts] = useState<EmailAccountConnection[]>([]);
  const [orgAccounts, setOrgAccounts] = useState<EmailAccountConnection[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const myAccounts = useMemo(
    () => accounts.filter((account) => account.is_owned_by_current_user),
    [accounts]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [form, setForm] = useState<EmailTemplateWritePayload>(EMPTY_TEMPLATE_FORM);
  const [sequencesPaused, setSequencesPaused] = useState(false);
  const [killSaving, setKillSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [ac, tm, org, seq] = await Promise.all([
        emailService.listAccounts(),
        emailService.listTemplates({ is_active: true }),
        isAdmin ? emailService.listOrganizationAccounts() : Promise.resolve([]),
        isAdmin ? sequenceService.getSettings() : Promise.resolve({ sequences_paused: false }),
      ]);
      setAccounts(ac);
      setOrgAccounts(org);
      setTemplates(tm);
      setSequencesPaused(seq.sequences_paused);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleKillSwitch = useCallback(async () => {
    const next = !sequencesPaused;
    if (next && !window.confirm('Pause all sequences? Scheduled touches will wait until you resume.')) {
      return;
    }
    setKillSaving(true);
    try {
      const res = await sequenceService.pauseAll(next);
      setSequencesPaused(res.sequences_paused);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update sequences.');
    } finally {
      setKillSaving(false);
    }
  }, [sequencesPaused]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('connected') === '1') {
      load();
      window.history.replaceState({}, '', '/settings/email');
    }
    if (q.get('error')) {
      setError(`Connection failed: ${q.get('error')}`);
      window.history.replaceState({}, '', '/settings/email');
    }
  }, [load]);

  const connect = async (provider: 'google' | 'microsoft') => {
    try {
      const { authorization_url } = await emailService.oauthStart(provider);
      window.location.href = authorization_url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start OAuth');
    }
  };

  const runAccountAction = async (action: () => Promise<void>) => {
    try {
      setError('');
      await action();
      await load();
    } catch (e) {
      if (getReconnectErrorMeta(e).requiresReconnect) {
        setError('Reconnect your mailbox in Settings → Email, then retry this action.');
      } else {
        setError(e instanceof Error ? e.message : 'Account update failed');
      }
    }
  };

  const createTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject_template.trim() || !form.body_template.trim()) return;
    try {
      setSavingTemplate(true);
      const payload: EmailTemplateWritePayload = {
        ...form,
        name: form.name.trim(),
        subject_template: form.subject_template.trim(),
        body_template: form.body_template.trim(),
        description: (form.description || '').trim(),
      };
      if (editingTemplateId) {
        await emailService.updateTemplate(editingTemplateId, payload);
      } else {
        await emailService.createTemplate(payload);
      }
      setEditingTemplateId(null);
      setForm(EMPTY_TEMPLATE_FORM);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingTemplate(false);
    }
  };

  const removeTemplate = async (template: EmailTemplate) => {
    if (!window.confirm(`Deactivate template "${template.name}"?`)) return;
    try {
      await emailService.deleteTemplate(template.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const beginEdit = (template: EmailTemplate) => {
    setEditingTemplateId(template.id);
    setForm({
      name: template.name,
      category: template.category,
      description: template.description || '',
      subject_template: template.subject_template,
      body_template: template.body_template,
      default_follow_up_delay_days: template.default_follow_up_delay_days,
      is_shared: template.is_shared,
      is_active: true,
    });
  };

  const resetForm = () => {
    setEditingTemplateId(null);
    setForm(EMPTY_TEMPLATE_FORM);
  };

  if (loading && !accounts.length && !templates.length) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-fg-muted">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-10">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Email</h1>
          <p className="text-fg-secondary mt-1 text-sm">
            Connect your mailbox to send email from leads, prospects, and clients. Admins can enable shared
            organization sending for connected accounts.
          </p>
          <p className="text-fg-muted mt-2 text-xs rounded-lg border border-amber-400/40 bg-amber-50/80 dark:bg-amber-950/25 px-3 py-2">
            <strong className="text-amber-950 dark:text-amber-100">Compliance:</strong> Contacts marked{' '}
            <span className="font-medium">Do Not Contact</span> are blocked from sends and excluded from eligible
            recipient counts. You can still see them in lists with a DNC badge.
          </p>
        </div>
        <Link
          to="/settings/email/performance"
          className="btn-secondary text-sm py-2 px-3 rounded-lg shrink-0 self-start"
        >
          Message performance
        </Link>
      </div>

      {error && (
        <div className="rounded-lg border border-danger-border bg-danger-bg text-danger-fg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <section className="card space-y-4">
        <h2 className="text-lg font-medium text-fg">Connected accounts</h2>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => connect('google')} className="btn-primary text-sm">
            Connect Google
          </button>
          <button type="button" onClick={() => connect('microsoft')} className="btn-secondary text-sm">
            Connect Microsoft
          </button>
        </div>
        {myAccounts.length === 0 ? (
          <p className="text-sm text-fg-muted border border-dashed border-border rounded-lg p-4">
            No accounts connected yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {myAccounts.map((a) => (
              <li
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface px-4 py-3 text-sm"
              >
                <div>
                  <div className="font-medium text-fg">
                    {a.email_address}{' '}
                    {a.is_primary ? (
                      <span className="text-xs font-normal text-primary">Primary</span>
                    ) : null}
                  </div>
                  <div className="text-fg-muted text-xs capitalize">{a.provider}</div>
                </div>
                <div className="flex gap-2">
                  {!a.is_primary && (
                    <button
                      type="button"
                      className="btn-secondary text-xs py-1 px-2"
                      onClick={() =>
                        runAccountAction(async () => {
                          await emailService.setPrimary(a.id);
                        })
                      }
                    >
                      Set primary
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-xs text-danger-fg hover:underline"
                    onClick={() => {
                      if (!window.confirm('Disconnect this account?')) return;
                      void runAccountAction(async () => {
                        await emailService.disconnect(a.id);
                      });
                    }}
                  >
                    Disconnect
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {isAdmin ? (
        <section className="card space-y-4">
          <div>
            <h2 className="text-lg font-medium text-fg">Organization Email Accounts</h2>
            <p className="text-sm text-fg-secondary mt-1">
              View all connected mailboxes in your organization and control shared sending.
            </p>
            <p className="text-xs text-fg-muted mt-2">
              Only admins and account owners can manage shared sending.
            </p>
          </div>
          {orgAccounts.length === 0 ? (
            <p className="text-sm text-fg-muted border border-dashed border-border rounded-lg p-4">
              No organization email accounts connected yet.
            </p>
          ) : (
            <ul className="space-y-3">
              {orgAccounts.map((account) => (
                <li
                  key={account.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface px-4 py-3 text-sm"
                >
                  <div>
                    <div className="font-medium text-fg">
                      {account.email_address}{' '}
                      <span className="text-xs font-normal text-fg-muted capitalize">({account.provider})</span>
                    </div>
                    <p className="text-xs text-fg-muted mt-1">
                      Connected by{' '}
                      {account.connected_by_name || account.connected_by_email || 'Unknown user'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-fg">
                      <input
                        type="checkbox"
                        checked={account.allow_org_sending}
                        onChange={(e) =>
                          void runAccountAction(async () => {
                            await emailService.updateAccountSharing(account.id, e.target.checked);
                          })
                        }
                      />
                      Shared with organization
                    </label>
                    <button
                      type="button"
                      className="text-xs text-danger-fg hover:underline"
                      onClick={() => {
                        if (!window.confirm(`Deactivate ${account.email_address}?`)) return;
                        void runAccountAction(async () => {
                          await emailService.disconnect(account.id);
                        });
                      }}
                    >
                      Deactivate
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {isAdmin ? (
        <section className="card space-y-3 border border-border">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-medium text-fg">Pause all sequences</h2>
              <p className="mt-1 text-sm text-fg-muted">
                {sequencesPaused
                  ? 'All sequences are paused. Scheduled touches will wait until you resume.'
                  : 'Sequences are running.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={sequencesPaused}
              disabled={killSaving}
              onClick={toggleKillSwitch}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${
                sequencesPaused ? 'bg-primary' : 'bg-fg/20'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                  sequencesPaused ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </section>
      ) : null}

      <section className="card space-y-4">
        <h2 className="text-lg font-medium text-fg">Follow-up templates</h2>
        <p className="text-sm text-fg-secondary">Create reusable email templates for leads, prospects, and clients.</p>
        <form onSubmit={createTemplate} className="space-y-3 border border-border rounded-lg p-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              className="input-field"
              placeholder="Template name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <select
              className="input-field"
              value={form.category}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, category: e.target.value as EmailTemplateCategory }))
              }
            >
              {EMAIL_TEMPLATE_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          <input
            className="input-field"
            placeholder="Subject"
            value={form.subject_template}
            onChange={(e) => setForm((prev) => ({ ...prev, subject_template: e.target.value }))}
          />
          <textarea
            className="input-field min-h-[120px]"
            placeholder="Body"
            value={form.body_template}
            onChange={(e) => setForm((prev) => ({ ...prev, body_template: e.target.value }))}
          />
          <textarea
            className="input-field min-h-[80px]"
            placeholder="Description (optional)"
            value={form.description || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <input
            className="input-field"
            type="number"
            min={0}
            max={365}
            placeholder="Default follow-up delay days (optional)"
            value={form.default_follow_up_delay_days ?? ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                default_follow_up_delay_days: e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={Boolean(form.is_shared)}
              onChange={(e) => setForm((prev) => ({ ...prev, is_shared: e.target.checked }))}
            />
            Share with everyone in my organization
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary text-sm" disabled={savingTemplate}>
              {savingTemplate ? 'Saving…' : editingTemplateId ? 'Update template' : 'Save template'}
            </button>
            {editingTemplateId && (
              <button type="button" className="btn-secondary text-sm" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>

        {templates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center">
            <p className="text-sm text-fg-secondary">No templates yet. Create your first follow-up template above.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {templates.map((t) => (
              <li key={t.id} className="rounded-lg border border-border px-3 py-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-fg">{t.name}</p>
                    <p className="text-xs text-fg-secondary mt-1">
                      {categoryLabel(t.category)} · Updated {formatDate(t.updated_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex rounded-full border border-border px-2 py-0.5 text-xs text-fg-secondary">
                      {t.is_system_template ? 'System' : t.is_shared ? 'Shared' : 'Personal'}
                    </span>
                    <span className="inline-flex rounded-full border border-border px-2 py-0.5 text-xs text-fg-secondary">
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {!t.is_system_template && (
                      <>
                        <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={() => beginEdit(t)}>
                          Edit
                        </button>
                        <button type="button" className="text-danger-fg text-xs" onClick={() => removeTemplate(t)}>
                          Deactivate
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {t.description ? <p className="mt-2 text-xs text-fg-secondary">{t.description}</p> : null}
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-lg border border-border bg-surface-muted/50 px-4 py-3">
          <h3 className="text-sm font-medium text-fg mb-2">Merge fields</h3>
          <p className="text-xs text-fg-secondary mb-2">
            Use these placeholders in subject/body. Unknown or missing values resolve to blank text.
          </p>
          <div className="flex flex-wrap gap-2">
                {EMAIL_TEMPLATE_MERGE_FIELDS.map((field) => (
              <code key={field} className="text-xs rounded border border-border px-2 py-1 text-fg-secondary">
                {field}
              </code>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmailSettings;
